import { laborPageKeysEquivalent } from "@/pages/clampqc/clampQcLaborClockState";

const REQUEST_KEY = "integrator_hub_labor_readonly_stay_request_v1";
const ACTIVE_KEY = "integrator_hub_labor_readonly_stay_active_v1";

/** How long a keep-open request stays valid before a later clock-out ignores it. */
export const LABOR_READ_ONLY_STAY_TTL_MS = 120_000;

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

type StayRequest = {
  page: string;
  tabId: string | null;
  at: number;
};

let requestStorage: () => StorageLike | null = () =>
  typeof localStorage === "undefined" ? null : localStorage;
let activeStorage: () => StorageLike | null = () =>
  typeof sessionStorage === "undefined" ? null : sessionStorage;

let activePageSnapshot: string | null | undefined;
const listeners = new Set<() => void>();

function readRequest(): StayRequest | null {
  const raw = requestStorage()?.getItem(REQUEST_KEY);
  if (raw == null || raw.trim() === "") return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StayRequest>;
    const page = typeof parsed.page === "string" ? parsed.page.trim() : "";
    const at = typeof parsed.at === "number" && Number.isFinite(parsed.at) ? parsed.at : Number.NaN;
    if (page === "" || !Number.isFinite(at)) return null;
    const tabId = typeof parsed.tabId === "string" && parsed.tabId.trim() !== "" ? parsed.tabId.trim() : null;
    return { page, tabId, at };
  } catch {
    return null;
  }
}

function readActivePageFromStorage(): string | null {
  const raw = activeStorage()?.getItem(ACTIVE_KEY);
  if (raw == null || raw.trim() === "") return null;
  try {
    const parsed = JSON.parse(raw) as { page?: unknown };
    const page = typeof parsed.page === "string" ? parsed.page.trim() : "";
    return page === "" ? null : page;
  } catch {
    return null;
  }
}

function commitActivePage(next: string | null): void {
  if (activePageSnapshot === next) return;
  activePageSnapshot = next;
  for (const listener of listeners) listener();
}

export function installLaborReadOnlyStayStorageForTests(request: StorageLike, active: StorageLike): void {
  requestStorage = () => request;
  activeStorage = () => active;
  activePageSnapshot = undefined;
}

export function resetLaborReadOnlyStayStorageForTests(): void {
  requestStorage = () => (typeof localStorage === "undefined" ? null : localStorage);
  activeStorage = () => (typeof sessionStorage === "undefined" ? null : sessionStorage);
  activePageSnapshot = undefined;
  listeners.clear();
}

/** Other tabs read this before the labor session ends. */
export function publishLaborReadOnlyStayRequest(input: { page: string; tabId: string | null; now?: number }): void {
  const page = input.page.trim();
  if (page === "") return;
  const tabId = input.tabId?.trim() ?? "";
  const request: StayRequest = {
    page,
    tabId: tabId === "" ? null : tabId,
    at: input.now ?? Date.now(),
  };
  requestStorage()?.setItem(REQUEST_KEY, JSON.stringify(request));
}

/** Drop a keep-open request that has not been claimed yet (plain clock out). */
export function withdrawLaborReadOnlyStayRequest(): void {
  requestStorage()?.removeItem(REQUEST_KEY);
}

/**
 * Owning labor tab: if a fresh request matches this page and tab, stay on the page
 * and remember read-only mode for this tab (including refresh).
 */
export function claimLaborReadOnlyStay(input: { page: string; tabId: string; now?: number }): boolean {
  const request = readRequest();
  if (request == null) return false;
  const now = input.now ?? Date.now();
  if (now - request.at > LABOR_READ_ONLY_STAY_TTL_MS) {
    withdrawLaborReadOnlyStayRequest();
    return false;
  }
  if (!laborPageKeysEquivalent(request.page, input.page)) return false;
  if (request.tabId != null && request.tabId !== input.tabId.trim()) return false;
  withdrawLaborReadOnlyStayRequest();
  const page = input.page.trim();
  activeStorage()?.setItem(ACTIVE_KEY, JSON.stringify({ page }));
  commitActivePage(page);
  return true;
}

export function getLaborReadOnlyStaySnapshot(): string | null {
  if (activePageSnapshot === undefined) {
    activePageSnapshot = readActivePageFromStorage();
  }
  return activePageSnapshot;
}

export function isLaborReadOnlyStayActive(page: string): boolean {
  const active = getLaborReadOnlyStaySnapshot();
  return active != null && laborPageKeysEquivalent(active, page);
}

export function clearLaborReadOnlyStay(): void {
  activeStorage()?.removeItem(ACTIVE_KEY);
  commitActivePage(null);
}

/** Leave read-only mode when this tab navigates to a different labor page. */
export function clearLaborReadOnlyStayIfPageDiffers(pageKey: string): void {
  const active = getLaborReadOnlyStaySnapshot();
  if (active == null) return;
  if (laborPageKeysEquivalent(active, pageKey)) return;
  clearLaborReadOnlyStay();
}

export function subscribeLaborReadOnlyStay(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
